import { useState, useEffect, useCallback } from "react";
import { fetchLockPeriods, fetchRecordUnlocks } from "../services/lockService";

/**
 * useLockStatus - ロック期間の取得・判定フック
 *
 * 登録済みの全ロック期間・全レコード単位解除を一度だけ取得し、isLocked(日付, 店舗, レジ) で
 * クライアント側判定する。日付ごとに個別問い合わせを行うと MonthlyDataPage 等で大量の通信が
 * 発生するため、一括取得してメモリ上で判定する方式にしている。
 *
 * **判定方式（カットオフ方式）**: 有効な（未解除の）LockPeriodの中で最大の EndDate を
 * 「締め切りカットオフ日」とし、その日以前はすべてロック中とみなす。
 * 範囲の集合方式（各LockPeriodの範囲内かどうかを個別判定）ではない。
 * 理由: 締め切り操作を一度忘れて範囲に抜けが生じても、それより新しい締め切りが
 * 登録されていればその抜けた範囲も含めてロックされる必要があるため
 * （共同開発者の実体験: 過去日付の誤編集事故防止が最優先という要望による）。
 *
 * **レコード単位の解除（RecordUnlock）**: カットオフより前でも、店舗×レジ×日付を1件指定して
 * 個別に編集可能にする例外。締め切り後に入力ミス等の個別修正が必要になった場合に使う
 * （旬単位の一括解除とは用途が異なる。LockPeriodの解除は登録ミスの取り消し専用）。
 *
 * @returns {{ lockPeriods: object[], recordUnlocks: object[], loading: boolean, isLocked: function, cutoffDate: string|null, refetch: function }}
 */
function useLockStatus() {
  const [lockPeriods, setLockPeriods] = useState([]);
  const [recordUnlocks, setRecordUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(() => {
    setLoading(true);
    return Promise.all([fetchLockPeriods(), fetchRecordUnlocks()])
      .then(([periods, unlocks]) => {
        setLockPeriods(periods);
        setRecordUnlocks(unlocks);
      })
      .catch((err) => console.error("ロック状態取得失敗:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // 有効なロックの中で最大の EndDate（締め切りカットオフ日）を求める
  const activeEndDates = lockPeriods
    .filter((p) => p.UnlockedAt == null)
    .map((p) => p.EndDate);
  const cutoffDate = activeEndDates.length > 0 ? activeEndDates.reduce((max, d) => (d > max ? d : max)) : null;

  // 指定の店舗・レジ・日付に有効なレコード単位解除があるかどうかを判定する
  const hasRecordException = (yyyymmdd, shopNo, regiNo) =>
    recordUnlocks.some(
      (u) =>
        u.RelockedAt == null &&
        u.YYYYMMDD === yyyymmdd &&
        u.ShopNo === shopNo &&
        u.RegiNo === regiNo,
    );

  // 指定日付・店舗・レジがロック中かどうかを判定する
  // （カットオフ日以前 かつ レコード単位の解除例外が存在しない場合にロック中とみなす）
  const isLocked = (yyyymmdd, shopNo, regiNo) =>
    cutoffDate != null &&
    yyyymmdd <= cutoffDate &&
    !hasRecordException(yyyymmdd, shopNo, regiNo);

  return { lockPeriods, recordUnlocks, loading, isLocked, cutoffDate, refetch };
}

export default useLockStatus;
